import React, { useEffect, useState } from 'react';
import axios from 'axios';
import VideoPlayer from './VideoPlayer/VideoPlayer';
import LanguageDetectionDialog from './VideoPlayer/LanguageDetectionDialog';
import { useSession } from '../context/SessionContext';
import './VideoModal.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const VideoModal = ({ resource, isOpen, onClose }) => {
  const videoUrl = resource?.id
    ? `${API_BASE}/resources/${resource.id}/download`
    : '';

  const { sessionLanguage } = useSession();
  const [showPlayer, setShowPlayer] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState(videoUrl);
  const [showDetectionDialog, setShowDetectionDialog] = useState(false);
  const [detectionData, setDetectionData] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);

  console.log('📺 [VideoModal] Current sessionLanguage:', sessionLanguage);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      // Reset states when opening modal
      setShowPlayer(false);
      setShowDetectionDialog(false);
      setDetectionData(null);
      setIsDetecting(false);
      setCurrentVideoUrl(videoUrl);

      // Iniciar detección de idioma cuando se abre el modal
      detectLanguage();
    } else {
      // Reset states when closing modal
      setShowPlayer(false);
      setShowDetectionDialog(false);
      setDetectionData(null);
      setIsDetecting(false);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose, sessionLanguage, resource?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const detectLanguage = async () => {
    if (!resource?.id) {
      setShowPlayer(true);
      return;
    }

    setIsDetecting(true);
    console.log('🎬 [VideoModal] Starting language detection for resource:', resource.id, 'target language:', sessionLanguage);

    try {
      // 0. First, check if translation already exists for this resource in sessionLanguage
      try {
        const checkTranslationResponse = await axios.get(
          `${API_BASE}/resources/${resource.id}/translation/${sessionLanguage}`,
          { timeout: 10000 }
        );

        if (checkTranslationResponse.data.hasTranslation) {
          // Translation already exists, play it directly
          const existingTaskId = checkTranslationResponse.data.taskId;
          console.log('✅ [VideoModal] Translated version found for taskId:', existingTaskId, 'language:', sessionLanguage);

          const playerUrl = `${API_BASE}/vts/player/${existingTaskId}`;
          setCurrentVideoUrl(playerUrl);
          setShowPlayer(true);
          setIsDetecting(false);
          return;
        }
      } catch (translationCheckError) {
        // If translation check fails, continue with normal detection flow
        console.log('ℹ️ [VideoModal] No cached translation found, continuing with detection');
      }

      // 1. Enviar solicitud de detección
      const response = await axios.post(
        `${API_BASE}/resources/detect-language`,
        { resourceId: resource.id },
        { timeout: 300000 }
      );

      const { taskId, alreadyDetected, detectedLanguage: dbLanguage } = response.data;

      // 1.1 Si el idioma ya estaba detectado en la DB
      if (alreadyDetected && dbLanguage) {
        console.log('✅ [VideoModal] Language already known:', dbLanguage);
        if (dbLanguage === sessionLanguage) {
          console.log('ℹ️ [VideoModal] Same language, playing directly');
          setCurrentVideoUrl(videoUrl);
          setShowPlayer(true);
        } else {
          setDetectionData({ detectedLanguage: dbLanguage, detectedConfidence: 1.0 });
          setShowDetectionDialog(true);
          setCurrentVideoUrl(videoUrl);
        }
        setIsDetecting(false);
        return;
      }

      console.log('📋 [VideoModal] Detection task created:', taskId);

      // 2. Polling para obtener resultado
      let detected = null;
      let attempts = 0;
      const maxAttempts = 120;

      while (attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 1000));

        const statusResponse = await axios.get(`${API_BASE}/tasks/${taskId}`);
        detected = statusResponse.data;

        if (detected.status === 'completed') {
          const detectedLang = detected.detectedLanguage;
          console.log('✅ [VideoModal] Detection completed:', detectedLang);

          // Persistir el idioma detectado en la DB para la próxima vez
          try {
            await axios.post(`${API_BASE}/resources/${resource.id}/detected-language`, {
              language: detectedLang
            });
            console.log('💾 [VideoModal] Persisted detected language in DB');
          } catch (persistError) {
            console.warn('⚠️ [VideoModal] Failed to persist detected language:', persistError);
          }

          // Comparar idiomas
          if (detectedLang && detectedLang !== sessionLanguage) {
            // Idiomas diferentes → mostrar diálogo
            setDetectionData(detected);
            setShowDetectionDialog(true);
            setCurrentVideoUrl(videoUrl);
          } else {
            // Mismo idioma → reproducir directamente
            console.log('ℹ️ [VideoModal] Same language, playing directly');
            setCurrentVideoUrl(videoUrl);
            setShowPlayer(true);
          }
          setIsDetecting(false);
          return;
        } else if (detected.status === 'failed') {
          console.error('❌ [VideoModal] Detection failed:', detected.error);
          setIsDetecting(false);
          setShowPlayer(true);
          return;
        }

        attempts++;
      }

      console.warn('⚠️ [VideoModal] Detection timeout');
      setIsDetecting(false);
      setShowPlayer(true);
    } catch (error) {
      console.error('❌ [VideoModal] Detection error:', error);
      setIsDetecting(false);
      setShowPlayer(true);
    }
  };

  const handlePlayOriginal = () => {
    console.log('📹 [VideoModal] Playing original video');
    setCurrentVideoUrl(videoUrl);
    setShowDetectionDialog(false);
    setShowPlayer(true);
  };

  const handlePlayTranslated = (translatedVideoUrl) => {
    console.log('🎬 [VideoModal] Playing translated video');
    setCurrentVideoUrl(translatedVideoUrl);
    setShowDetectionDialog(false);
    setShowPlayer(true);
  };

  const handleCancelDetection = () => {
    console.log('✕ [VideoModal] Detection cancelled, closing modal');
    setShowDetectionDialog(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="video-modal-overlay" onClick={onClose}>
      <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Mostrar diálogo de detección si está disponible */}
        {showDetectionDialog && detectionData && (
          <LanguageDetectionDialog
            isOpen={true}
            detectedLanguage={detectionData.detectedLanguage}
            detectedConfidence={parseFloat(detectionData.detectedConfidence)}
            sessionLanguage={sessionLanguage}
            videoTitle={resource?.title || 'Video'}
            resourceId={resource?.id}
            onPlayOriginal={handlePlayOriginal}
            onPlayTranslated={handlePlayTranslated}
            onCancel={handleCancelDetection}
          />
        )}

        {/* Mostrar spinner de detección mientras se analiza */}
        {isDetecting && (
          <div className="video-modal-detecting">
            <div className="spinner"></div>
            <p>🔍 Analizando idioma del video...</p>
          </div>
        )}

        {/* Mostrar player cuando esté listo */}
        {showPlayer && (
          <>
            <div className="video-modal-header">
              <h2>{resource?.title || 'Video'}</h2>
            </div>
            <div className="video-modal-player">
              <VideoPlayer
                videoUrl={currentVideoUrl}
                title={resource?.title}
                onClose={onClose}
                downloadUrl={currentVideoUrl}
                fileName={resource?.originalResource?.fileName || resource?.fileName || 'video'}
                resourceId={resource?.id}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoModal;
